import type { Method } from "axios";
import request from "@user-center/utils/request";
import { getToken } from "@user-center/utils/auth";
import i18n from "@user-center/utils/i18n";
import settings from "@user-center/settings";
import { ElMessage, inputEmits } from "element-plus";
import { Decimal } from "decimal.js";
import { get } from "lodash-es";
/**
 * 下载
 * @param {string} url 传入请求地址
 * @param {object} data 参数对象
 * 注：如果返回是base64类型，或者是需要自己规定命名的话，需要在data中添加 storageName 字段，用于确定下载的文件名
 * @param {string} method 请求类型，默认post
 * @param {string} fileName 自定义文件名(文件名+后缀名)

 */
export const wmsDownLoad = async (
  url = "",
  data: any = {},
  method: Method = "post",
  fileName?: any,
  headers?: any
) => {
  let res: any;
  if (method !== "get") {
    res = await request(
      {
        method, // 配置请求方式
        url, // 配置url
        data,
        timeout: 10 * 60 * 1000,
        responseType: "blob",
        headers,
        onDownloadProgress: (progressEvent) => {
          console.log('onDownloadProgress', progressEvent);
          console.log(`已下载: ${progressEvent.loaded} 字节`);
          if (progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            console.log(`Download Progress: ${progress}%`);
          }
        },
      },
      {
        returnAll: true,
        loading: true,
      }
    );
  } else {
    res = await request(
      {
        method, // 配置请求方式
        url, // 配置url
        params: data,
        timeout: 10 * 60 * 1000,
        responseType: "blob",
        headers,
        onDownloadProgress: (progressEvent) => {
          console.log('onDownloadProgress', progressEvent);
          if (progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            console.log(`Download Progress: ${progress}%`);
          }
        },
      },
      {
        returnAll: true,
        loading: true,
      }
    );
  }

  const resData = res.data;
  const fileReader = new FileReader();
  fileReader.readAsText(resData as Blob);
  fileReader.onload = function () {
    try {
      // 说明是普通对象数据，后台转换失败
      const responseJson = JSON.parse(
        this.result as string | ArrayBuffer | any
      );
      // 判断接口失败的情况
      if (responseJson.result === false || responseJson.success === false) {
        ElMessage({
          message: responseJson.message || responseJson.msg,
          type: "warning",
        });
        return;
      }
      if (responseJson.data.storageName && !fileName) {
        fileName = responseJson.data.storageName;
      }
      _fileDownloadHandle(fileName, responseJson.data.fileBytes, "base64", res.headers);
    } catch (err) {
      // MIME形式下可以直接取 content-disposition 的信息作为文件名
      if (res.headers["content-disposition"] && !fileName) {
        fileName = res.headers["content-disposition"].split("''")[1];
        if (!fileName) fileName = res.headers["content-disposition"].split("=")[1];
        fileName = decodeURI(fileName);
      }
      _fileDownloadHandle(fileName, res.data, "blob", res.headers);
    }
  };
};


function nwFileDownload(filename = '', data: Blob, headers = { contentType: '' }) {
  const mimeType = headers.contentType || "application/octet-stream";

  const mimeMap: Record<string, string> = {
    "application/zip": ".zip",
    "application/pdf": ".pdf",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    "application/msword": ".doc",
    "application/vnd.ms-excel": ".xls",
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "text/plain": ".txt",
  };

  let fileExt = mimeMap[mimeType] || "";

  if (!filename.includes(".")) {
    filename += fileExt;
  }

  const newBlob = new Blob([data], { type: mimeType })
  const url = URL.createObjectURL(newBlob)


  chrome.downloads.download({ url, filename, saveAs: true }, (downloadId: number) => {
    if (chrome.runtime.lastError) {
      console.error("download", chrome.runtime.lastError.message);
      alert(chrome.runtime.lastError.message);
      return;
    }

    console.log("Download started:", { downloadId, filename });

    // 监听下载进度
    chrome.downloads.onChanged.addListener((delta) => {
      console.log("Download onChanged:", delta);
      if (delta.id === downloadId && delta.state) {
        if (delta.state.current === "in_progress") {
          if (delta.bytesReceived && delta.totalBytes) {
            const progress = Math.round(
              (delta.bytesReceived / delta.totalBytes) * 100
            );
            console.log(`Download progress: ${progress}%`);
          }
        } else if (delta.state.current === "complete") {
          console.log("Download complete:", filename);
        } else if (delta.state.current === "interrupted") {
          console.error("Download interrupted:", filename);
        }
      }
    });

    // 处理危险文件
    chrome.downloads.acceptDanger(downloadId, () => {
      if (chrome.runtime.lastError) {
        console.error("acceptDanger", chrome.runtime.lastError.message);
      }
    });
  });
}

function _fileDownloadHandle(fileName = "", data: any, type = "base64", headers: any) {
  const blob = _dataURLtoBlob(data, type);
  if (window.nw) {
    nwFileDownload(fileName, blob, headers)
    return
  }
  const downloadElement = document.createElement("a");
  // 创建下载的链接
  const href = window.URL.createObjectURL(blob);
  downloadElement.href = href;
  // 下载后文件名
  downloadElement.download = fileName;
  document.body.appendChild(downloadElement);
  // 点击下载
  downloadElement.click();
  // 下载完成移除元素
  document.body.removeChild(downloadElement);
  // 释放掉blob对象
  window.URL.revokeObjectURL(href);
}

// 把返回的base64流转为blob流
function _dataURLtoBlob(dataurl: any, type = "base64") {
  if (type === "blob") {
    return new Blob([dataurl]);
  }
  const bstr = atob(dataurl);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr]);
}
// 下载 连接文件
export function _fileDownloadlinke(fileName = "", url = "") {
  // 准备一个 a 标签用于下载
  const downloadElement = document.createElement("a");
  downloadElement.href = url;
  // 下载后文件名
  downloadElement.download = fileName;
  document.body.appendChild(downloadElement);
  // 点击下载
  downloadElement.click();
  // 下载完成移除元素
  document.body.removeChild(downloadElement);
}
export const paymentType = [
  { value: "100", label: "余额支付" },
  { value: "200", label: "货到付款" },
  { value: "300", label: "代收货款" },
  { value: "400", label: "线下支付" },
];
/**
 * 单号状态处理
 */
const SingleNumberData = [
  { packageStatus: "10", name: "待支付" },
  { packageStatus: "50", name: "待发货" },
  { packageStatus: "100", name: "创建" },
  { packageStatus: "110", name: "预报⼊库" },
  { packageStatus: "120", name: "预处理异常" },
  { packageStatus: "200", name: "生成发运订单" },
  { packageStatus: "210", name: "分配中" },
  { packageStatus: "220", name: "分配失败" },
  { packageStatus: "250", name: "部分分配" },
  { packageStatus: "300", name: "分配完成" },
  { packageStatus: "400", name: "等待拣货" },
  { packageStatus: "450", name: "部分拣货" },
  { packageStatus: "500", name: "拣货完成" },
  { packageStatus: "600", name: "播种完成" },
  { packageStatus: "620", name: "部分复核" },
  { packageStatus: "650", name: "完全复核" },
  { packageStatus: "670", name: "部分装箱" },
  { packageStatus: "700", name: "完全装箱" },
  { packageStatus: "720", name: "集运装箱中" },
  { packageStatus: "723", name: "集运部分装箱" },
  { packageStatus: "724", name: "集运装箱完成" },
  { packageStatus: "726", name: "集运交接中" },
  { packageStatus: "727", name: "集运部分交接" },
  { packageStatus: "728", name: "集运交接完成" },
  { packageStatus: "730", name: "海外已入库" },
  { packageStatus: "732", name: "海外完全入库" },
  { packageStatus: "740", name: "海外已上架" },
  { packageStatus: "742", name: "海外完全上架" },
  { packageStatus: "750", name: "部分发运" },
  { packageStatus: "800", name: "完全发运" },
  { packageStatus: "900", name: "发货确认" },
  { packageStatus: "990", name: "订单取消" },
  { packageStatus: "999", name: "出库单完结" },
];
export const processingOrderNumberLableFn = (
  afferentData: any,
  key = "packageStatus"
) => {
  if (Object.keys(afferentData).length > 0) {
    const data: any = SingleNumberData.find((item) => {
      return item.packageStatus === afferentData[key];
    });
    return data ? data.name : afferentData[key];
  } else {
    return "";
  }
};
/**
 * 订单状态
 */
export const PaymentStatusList = [
  { value: 0, label: "订单未支付" },
  { value: 1, label: "订单已支付" },
  { value: 6, label: "订单超时取消" },
  { value: 13, label: "用户取消订单" },
  { value: 14, label: "订单未支付,待核重" },
  { value: 15, label: "订单未支付,已核重" },
  { value: 16, label: "订单未支付,COD订单" },
  { value: 17, label: "订单已审核" },
  { value: 18, label: "订单已下发" },
];
export const SetSaleOrderStatus = (row: any) => {
  let Status = "";
  if (![null, undefined].includes(row.saleOrderStatus)) {
    switch (Number(row.saleOrderStatus)) {
      case 0:
        Status = "订单未支付";
        break;
      case 1:
        Status = "订单已支付";
        break;
      case 6:
        Status = "订单超时取消";
        break;
      case 13:
        Status = "用户取消订单";
        break;
      case 14:
        Status = "订单未支付,待核重";
        break;
      case 15:
        Status = "订单未支付,已核重";
        break;
      case 16:
        Status = "订单未支付,COD订单";
        break;
      case 17:
        Status = "订单已审核";
        break;
      case 18:
        Status = "订单已下发";
        break;
    }
  }
  return Status;
};
/**
 * 复制功能
 */
export const copyText = (text: string, hintInfor = "复制,成功!") => {
  let container: any = document.getElementsByClassName("copyText-container")[0];
  if (!container) {
    const copyInput = document.createElement("textarea");
    copyInput.className = "copyText-container";
    document.body.appendChild(copyInput);
    container = copyInput;
  }
  container.value = "";
  container.value = text;
  container.select(); // 选择对象
  document.execCommand("Copy"); // 执行浏览器复制命令
  if (hintInfor) {
    ElMessage({
      type: "success",
      message: hintInfor,
    });
  }
};
export function isPositiveInteger(str: any): boolean {
  // 判断是否为空字符串
  if (str === "" || str === null || str === undefined) {
    return false;
  }
  // 判断是否为数字
  if (!/^\d+$/.test(str)) {
    return false;
  }
  // 判断是否为正整数
  if (parseInt(str) <= 0) {
    return false;
  }
  return true;
}
type numericalValue = number | string | undefined | null;
// 加法计算
export const addition = (value: numericalValue, end: numericalValue) => {
  value = value === null || value === undefined ? 0 : value;
  end = end === null || end === undefined ? 0 : end;
  const vlu = new Decimal(value).add(new Decimal(end)).toNumber();
  return vlu as number;
};
// 减法计算
export const subtraction = (value: numericalValue, end: numericalValue) => {
  value = value === null || value === undefined ? 0 : value;
  end = end === null || end === undefined ? 0 : end;
  const vlu = new Decimal(value).sub(new Decimal(end)).toNumber();
  return vlu as number;
};
// 乘法计算
export const multiplication = (value: numericalValue, end: numericalValue) => {
  value = value === null || value === undefined ? 0 : value;
  end = end === null || end === undefined ? 0 : end;
  const vlu = new Decimal(value).mul(new Decimal(end)).toNumber();
  return vlu as number;
};
// 除法计算
export const division = (value: numericalValue, end: numericalValue) => {
  value = value === null || value === undefined ? 0 : value;
  if (!end) return " ";
  const vlu = new Decimal(value).div(new Decimal(end)).toNumber();
  return vlu as number;
};
// 余数计算
export const remainder = (value: numericalValue, end: numericalValue) => {
  value = value === null || value === undefined ? 0 : value;
  end = end === null || end === undefined ? 0 : end;
  const a: any = new Decimal(value);
  const b: any = new Decimal(end);
  const remainder = a.modulo(b);
  return remainder.toString();
};
export const valueIsNumber = (value: any) => {
  if (
    value == null ||
    value === "" ||
    value == undefined ||
    typeof value === "undefined" ||
    isNaN(value)
  ) {
    return false;
  } else {
    return true;
  }
};
// 从url获取参数值
export const getUrlValByKey = (key: string) => {
  let res = null;
  const resMatch =
    location.search &&
    new RegExp(`(\\?|\\&)${key}=(\\w+)`, "g").exec(location.search);
  if (resMatch && resMatch[2]) {
    res = resMatch[2];
  }
  return res;
};

// 从url获取参数值
export const makeH5IframeUrl = (url: string): string => {
  const res: string = (settings.collectionH5SiteUrl + url).replace("https://","//").replace("http://","//");
  const source =
    getUrlValByKey("source") || window.localStorage.getItem("source") || "";
  const locale =
    getUrlValByKey("locale") || window.localStorage.getItem("locale") || "";
  const inviteCode =
    getUrlValByKey("inviteCode") || window.localStorage.getItem("inviteCode") || "";
  const csPersonCode =
    getUrlValByKey("csPersonCode") || window.localStorage.getItem("csPersonCode") || "";


  const language = get(i18n,'global.locale.value')|| window.localStorage.getItem('now-locale')
  const token = getToken();
  let languageCodeTransferMap={
    "ja_JP":'jp',
    "zh-CN":'zh-Hans',
    "zh-TW":'zh-Hant',
    "vi_VN":'vi',
  }

  const query = {
    isPcIframe: true,
    currentParentUrl: encodeURIComponent(window.location.href),
    source,
    locale,
    inviteCode,
    csPersonCode,
    setLanguage:languageCodeTransferMap[language]||language,
    urlToken: token,
   
  };
  
  let result = res;
  let joinStr = !result.includes("?") ? "?" : "&";
  Object.keys(query).forEach((key) => {
    if (query[key]) {
      result += `${joinStr}${key}=${query[key]}`;
      joinStr = "&";
    }
  });
  return result
};

// 转换代理虾皮图片地址
export function transformShopeeUrl(sourceUrl: string): string {
  // 检查URL的格式是否正确
  const sourcePattern = /^https:\/\/cf\.shopee\.tw\/(.+)$/;
  const match = sourceUrl.match(sourcePattern);

  if (!match) {
    // throw new Error('Invalid source URL format');
    return sourceUrl;
  }

  const path = match[1];
  const targetUrl = `http://cf-shopee-tw.proxy3.omszt.com/${path}`;

  return targetUrl;
}

// 从url判断设置移动端url
export const urlToSetCollectionH5SiteUrl = (url: string): void => {
  // 动态移动端iframe
  if(!settings.isPrefixCollectionH5SiteUrl) return;
  let siteCode = "";
  try {
    siteCode = url.replace(/https?:\/\//g, "").split(".")[0];
  } catch (e) {
    console.log(e);
  }
  let urlIsTest = false;
  const siteSplit = location.host.split(".");
  if ((siteSplit[1] && siteSplit[1].indexOf("test") >= 0)||location.host.indexOf('localhost')>-1 ){
    urlIsTest = true;
  }
  if (settings.collectionH5SiteCodeMap[siteCode]) {
    settings.collectionH5SiteUrl =
      window.location.protocol + settings.collectionH5SiteCodeMap[siteCode];
  } else {
    if (window.location.host.indexOf("127.0.0.1") >= 0) {
      settings.collectionH5SiteUrl = "http://localhost:8033";
      return;
    }else if(window.location.host.indexOf("xjhy-webpage.supplychain-platform.oms-sit.omszt.com:7788") >= 0) {
      settings.collectionH5SiteUrl = window.location.protocol +"//xjhy.m-shop-test.omszt.com:7888";
      return;
    }
    settings.collectionH5SiteUrl =
      window.location.protocol +
      "//" +
      siteCode +
      `.m-shop${!urlIsTest ? "" : "-test"}.omszt.com${
        urlIsTest ? ":7888" : ""
      }`;
  }
};

export function isWeChatBrowser(): boolean {
  var ua: string = window.navigator.userAgent.toLowerCase();
  return ua.indexOf("micromessenger") !== -1;
}

export function debugerOnline(argms: any): void {
  if (typeof window.__globalDebugerOnline == "function") {
    window.__globalDebugerOnline(argms);
  }
}
